import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Progress } from '@vaiu/ui/progress';
import { i18n } from '../utils/i18n';

/**
 * ResetPasswordWithTokenPage
 * 
 * This page handles the actual password reset when a user clicks
 * the link in their email with a reset token.
 */
export function ResetPasswordWithTokenPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    // Validate that we have a token
    if (!token) {
      setError(i18n.__('resetPassword.noToken'));
      setValidatingToken(false);
      return;
    }

    // Token format validation (basic check)
    if (token.length < 20) {
      setError(i18n.__('resetPassword.invalidToken'));
      setValidatingToken(false);
      return;
    }

    // Mark token as valid for now (actual validation happens on submit)
    setTokenValid(true);
    setValidatingToken(false);
  }, [token]);

  const calculatePasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '', percentage: 0 };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,  // Just for strength indicator, not enforced
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      symbols: /[^A-Za-z0-9]/.test(password)
    };
    
    Object.values(checks).forEach(check => {
      if (check) score++;
    });
    
    const percentage = (score / 5) * 100;
    
    if (score <= 1) {
      return { score, label: i18n.__('signup.passwordStrength.veryWeak'), color: 'bg-red-500', percentage };
    } else if (score === 2) {
      return { score, label: i18n.__('signup.passwordStrength.weak'), color: 'bg-orange-500', percentage };
    } else if (score === 3) {
      return { score, label: i18n.__('signup.passwordStrength.fair'), color: 'bg-yellow-500', percentage };
    } else if (score === 4) {
      return { score, label: i18n.__('signup.passwordStrength.strong'), color: 'bg-green-500', percentage };
    } else {
      return { score, label: i18n.__('signup.passwordStrength.veryStrong'), color: 'bg-green-600', percentage };
    }
  };

  const validateForm = () => {
    if (!newPassword) {
      setError(i18n.__('resetPassword.errors.newPasswordRequired'));
      return false;
    }

    if (!confirmPassword) {
      setError(i18n.__('resetPassword.errors.confirmPasswordRequired'));
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError(i18n.__('resetPassword.errors.passwordsMismatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      // Call the API endpoint to reset password with token
      const response = await fetch('/api/v1/auth/reset-password-with-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to reset password');
      }

      setSuccess(true);
      
      // If the backend returns a token, store it for automatic login
      if (data.token) {
        // Store the JWT token
        localStorage.setItem('authToken', data.token);
        if (data.username) {
          localStorage.setItem('username', data.username);
        }
        if (data.userId) {
          localStorage.setItem('userId', data.userId);
        }
        
        // Redirect to home page (already logged in)
        setTimeout(() => {
          window.location.href = '/'; // Full page reload to reinitialize auth context
        }, 2000);
      } else {
        // Fallback: Redirect to login if no token provided
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Password reset successful. Please log in with your new password.' 
            } 
          });
        }, 3000);
      }

    } catch (err: any) {
      console.error('Password reset error:', err);
      
      let errorMessage = err.message || 'Failed to reset password';
      
      // Handle specific error cases
      if (errorMessage.includes('expired')) {
        errorMessage = i18n.__('resetPassword.errors.expired');
      } else if (errorMessage.includes('Invalid token')) {
        errorMessage = i18n.__('resetPassword.errors.invalid');
      } else if (errorMessage.includes('Network')) {
        errorMessage = i18n.__('resetPassword.errors.network');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-600">{i18n.__('resetPassword.validating')}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl text-red-600">{i18n.__('resetPassword.invalidLink')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {i18n.__('resetPassword.linkExpired')}
              </p>
              
              <Button
                className="w-full"
                onClick={() => navigate('/forgot-password')}
              >
                {i18n.__('resetPassword.requestNewLink')}
              </Button>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                {i18n.__('resetPassword.backToLogin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl">
              {i18n.__('vaiu.reset_password.success_title') || 'Password Reset Successful'}
            </CardTitle>
            <CardDescription className="mt-2">
              {i18n.__('vaiu.reset_password.success_description') || 
                'Your password has been reset successfully.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {i18n.__('resetPassword.loggingIn')}
              </AlertDescription>
            </Alert>
            
            <Button
              className="w-full"
              onClick={() => window.location.href = '/'}
            >
              {i18n.__('vaiu.continue_to_app') || 'Continue to App'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <Button
            variant="ghost"
            size="sm"
            className="w-fit -ml-2 mb-2"
            onClick={() => navigate('/login')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.__('vaiu.back') || 'Back'}
          </Button>
          
          <div className="flex items-center space-x-2 mb-2">
            <Lock className="w-6 h-6 text-blue-600" />
            <CardTitle className="text-2xl">
              {i18n.__('vaiu.reset_password.form_title') || 'Reset Your Password'}
            </CardTitle>
          </div>
          
          <CardDescription>
            {i18n.__('vaiu.reset_password.form_description') || 
              'Enter your new password below'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* New Password Field */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {i18n.__('vaiu.new_password') || 'New Password'}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={i18n.__('resetPassword.passwordPlaceholder')}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  className={error && error.includes('password') ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="space-y-1">
                  <Progress value={passwordStrength.percentage} className="h-2" />
                  <p className={`text-xs ${
                    passwordStrength.score <= 2 ? 'text-red-600' : 
                    passwordStrength.score === 3 ? 'text-yellow-600' : 
                    'text-green-600'
                  }`}>
                    {i18n.__('resetPassword.strength', { strength: passwordStrength.label })}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {i18n.__('vaiu.confirm_password') || 'Confirm Password'}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={i18n.__('resetPassword.confirmPlaceholder')}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  className={error && error.includes('match') ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Password Match Indicator */}
              {confirmPassword && (
                <p className={`text-xs ${
                  newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'
                }`}>
                  {newPassword === confirmPassword ? i18n.__('resetPassword.match') : i18n.__('resetPassword.noMatch')}
                </p>
              )}
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading || !newPassword || !confirmPassword}
            >
              {loading 
                ? (i18n.__('vaiu.resetting_password') || 'Resetting Password...') 
                : (i18n.__('vaiu.reset_password.submit') || 'Reset Password')}
            </Button>

            {/* Help Text */}
            <div className="text-center text-sm text-gray-600">
              {i18n.__('vaiu.reset_password.need_help') || 'Need help?'}{' '}
              <Button
                type="button"
                variant="link"
                className="p-0 h-auto text-blue-600 hover:underline"
                onClick={() => navigate('/forgot-password')}
              >
                {i18n.__('vaiu.reset_password.request_new') || 'Request a new reset link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
